import { useEffect, useRef, useState } from 'react';
import Countdown from './shared/Countdown';
import {
  DecoFrameTop,
  DecoFrameBottom,
  DecoMonogram,
  DecoDividerSmall,
  DecoScallop,
  SunburstDivider,
  WeddingRings,
} from './shared/Decorations';
import {
  IconShirt,
  IconGift,
  IconCamera,
  IconChurch,
  IconMapPin,
  IconBook,
} from '../components/ui/Icons';
import Gallery from './shared/Gallery';
import AudioPlayer from '../components/AudioPlayer';

/* ============================================================
   SISTEMA DE DISEÑO — Tokens
   ============================================================ */
const T = {
  gold: '#d4af37',
  goldLight: '#f4e5a1',
  goldDeep: '#a8842a',
  goldSoft: '#b8945f',
  bg: '#08080f',
  bgAlt: '#0d0d1a',
  card: 'rgba(18, 18, 36, 0.55)',
  cardSolid: 'rgba(13, 13, 26, 0.9)',
  border: 'rgba(212, 175, 55, 0.18)',
  borderStrong: 'rgba(212, 175, 55, 0.4)',
  text: '#f5f2ea',
  textDim: 'rgba(245, 242, 234, 0.72)',
  muted: '#7d7a6e',
  serif: "'Cinzel', serif",
  script: "'Cormorant Garamond', serif",
  display: "'Playfair Display', serif",
  sans: "'Inter', system-ui, sans-serif",
};

const GOLD_GRADIENT = 'linear-gradient(135deg, #f4e5a1 0%, #d4af37 35%, #a8842a 70%, #d4af37 100%)';
const GOLD_GRADIENT_SOFT = 'linear-gradient(135deg, rgba(244,229,161,0.9) 0%, rgba(212,175,55,0.85) 50%, rgba(168,132,42,0.9) 100%)';
const BG_MAIN = 'radial-gradient(ellipse at top, #12122a 0%, #08080f 55%, #050509 100%)';

/* ============================================================
   ESTILOS GLOBALES
   ============================================================ */
const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=Inter:wght@300;400;500;600&display=swap');

  @keyframes shimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes floatPetal {
    0%   { transform: translateY(-10vh) translateX(0) rotate(0deg); opacity: 0; }
    10%  { opacity: 0.55; }
    90%  { opacity: 0.55; }
    100% { transform: translateY(110vh) translateX(80px) rotate(360deg); opacity: 0; }
  }
  @keyframes pulseGlow {
    0%, 100% { box-shadow: 0 0 30px rgba(212,175,55,0.08), inset 0 0 30px rgba(212,175,55,0.03); }
    50%      { box-shadow: 0 0 60px rgba(212,175,55,0.18), inset 0 0 40px rgba(212,175,55,0.06); }
  }
  @keyframes scrollDot {
    0%   { transform: translateY(0);    opacity: 0; }
    40%  { opacity: 1; }
    100% { transform: translateY(32px); opacity: 0; }
  }
  @keyframes monogramFloat {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50%      { transform: translateY(-5px) rotate(0.6deg); }
  }
  @keyframes ringPulse {
    0%, 100% { transform: scale(1) rotate(0deg);   opacity: 0.9; }
    50%      { transform: scale(1.03) rotate(1deg); opacity: 1; }
  }
  @keyframes lineGrow {
    from { transform: scaleX(0); }
    to   { transform: scaleX(1); }
  }
  @keyframes softFade {
    from { opacity: 0; }
    to   { opacity: 0.6; }
  }

  .boda-shimmer {
    background: linear-gradient(90deg, #d4af37 0%, #f4e5a1 20%, #fff8dc 40%, #f4e5a1 55%, #d4af37 75%, #a8842a 100%);
    background-size: 250% auto;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: shimmer 8s linear infinite;
  }
  .boda-gold-text {
    background: ${GOLD_GRADIENT};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .boda-card {
    position: relative;
    background: ${T.card};
    backdrop-filter: blur(14px) saturate(120%);
    -webkit-backdrop-filter: blur(14px) saturate(120%);
    border: 1px solid ${T.border};
    transition: transform 0.6s cubic-bezier(0.22,1,0.36,1),
                border-color 0.6s,
                box-shadow 0.6s,
                background 0.6s;
  }
  .boda-card::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(244,229,161,0.06) 0%, transparent 40%, transparent 60%, rgba(212,175,55,0.04) 100%);
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.6s;
  }
  .boda-card:hover {
    transform: translateY(-8px);
    border-color: rgba(212,175,55,0.55);
    box-shadow: 0 30px 70px -30px rgba(212,175,55,0.5),
                0 0 0 1px rgba(212,175,55,0.1) inset;
    background: rgba(24, 24, 46, 0.7);
  }
  .boda-card:hover::before { opacity: 1; }

  .boda-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 14px 34px;
    font-family: ${T.sans};
    font-size: 11px;
    letter-spacing: 0.28em;
    text-transform: uppercase;
    color: ${T.bg};
    background: ${GOLD_GRADIENT};
    border: none;
    cursor: pointer;
    overflow: hidden;
    transition: all 0.5s cubic-bezier(0.22,1,0.36,1);
    text-decoration: none;
    font-weight: 600;
  }
  .boda-btn::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%);
    transform: translateX(-100%);
    transition: transform 0.8s;
  }
  .boda-btn:hover::after { transform: translateX(100%); }
  .boda-btn:hover {
    box-shadow: 0 20px 40px -15px rgba(212,175,55,0.6);
    transform: translateY(-2px);
  }

  .boda-petal {
    position: fixed;
    top: 0;
    pointer-events: none;
    z-index: 1;
  }

  .boda-hairline {
    height: 1px;
    background: linear-gradient(90deg, transparent, ${T.gold}55, transparent);
  }

  .boda-eyebrow {
    font-family: ${T.sans};
    font-size: 10px;
    letter-spacing: 0.5em;
    text-transform: uppercase;
    font-weight: 500;
    color: ${T.gold};
  }

  .boda-drop-cap::first-letter {
    font-family: ${T.display};
    font-size: 4.5rem;
    line-height: 0.85;
    float: left;
    margin: 8px 14px 0 0;
    background: ${GOLD_GRADIENT};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    font-style: italic;
    font-weight: 500;
  }

  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.001ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.001ms !important;
    }
  }
`;

/* ============================================================
   HOOKS
   ============================================================ */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -80px 0px', ...options }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

function Reveal({ children, delay = 0, y = 44, className = '', as: Tag = 'div' }) {
  const { ref, visible } = useReveal();
  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : `translateY(${y}px)`,
        transition: `opacity 1.1s cubic-bezier(0.22,1,0.36,1) ${delay}s, transform 1.1s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        willChange: 'opacity, transform',
      }}
    >
      {children}
    </Tag>
  );
}

function useParallax(speed = 0.3) {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    let raf;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        setOffset((window.innerHeight - rect.top) * speed * 0.1);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [speed]);
  return { ref, offset };
}

/* ============================================================
   ORNAMENTOS SVG
   ============================================================ */
const Ornament = ({ className = '', color = T.gold }) => (
  <svg viewBox="0 0 200 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="0" y1="12" x2="72" y2="12" stroke={color} strokeWidth="0.8" />
    <line x1="128" y1="12" x2="200" y2="12" stroke={color} strokeWidth="0.8" />
    <path d="M88 12 L100 4 L112 12 L100 20 Z" stroke={color} strokeWidth="1" fill="none" />
    <path d="M94 12 L100 8 L106 12 L100 16 Z" fill={color} opacity="0.7" />
    <circle cx="80" cy="12" r="1.6" fill={color} opacity="0.6" />
    <circle cx="120" cy="12" r="1.6" fill={color} opacity="0.6" />
  </svg>
);

const CornerFrame = ({ className = '', color = T.gold }) => (
  <svg viewBox="0 0 40 40" className={className} fill="none">
    <path d="M2 38 V2 H38" stroke={color} strokeWidth="1" />
    <path d="M8 32 V8 H32" stroke={color} strokeWidth="0.6" opacity="0.6" />
    <circle cx="2" cy="2" r="2" fill={color} />
  </svg>
);

const Flourish = ({ className = '', color = T.gold }) => (
  <svg viewBox="0 0 300 40" className={className} fill="none">
    <path
      d="M10 20 Q60 20 90 12 Q120 4 150 20 Q180 36 210 28 Q240 20 290 20"
      stroke={color}
      strokeWidth="1"
      fill="none"
      opacity="0.7"
    />
    <circle cx="150" cy="20" r="3" fill={color} />
    <circle cx="10" cy="20" r="1.5" fill={color} opacity="0.6" />
    <circle cx="290" cy="20" r="1.5" fill={color} opacity="0.6" />
  </svg>
);

/* ============================================================
   PÉTALOS VECTORIALES
   ============================================================ */
function PetalSvg({ size = 16, color = 'rgba(212,175,55,0.3)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path
        d="M12 2 C8 6, 4 9, 4 14 C4 18, 8 22, 12 22 C16 22, 20 18, 20 14 C20 9, 16 6, 12 2 Z"
        fill={color}
      />
      <path
        d="M12 4 C10 7, 7 10, 7 14 C7 17, 9.5 19.5, 12 20"
        stroke="rgba(255,255,255,0.15)"
        strokeWidth="0.6"
        fill="none"
      />
    </svg>
  );
}

function FloatingPetals({ count = 18 }) {
  const petals = useRef(
    Array.from({ length: count }).map(() => ({
      left: Math.random() * 100,
      delay: Math.random() * 15,
      duration: 14 + Math.random() * 12,
      size: 10 + Math.random() * 14,
      rotate: Math.random() * 360,
      opacity: 0.2 + Math.random() * 0.35,
    }))
  ).current;

  return (
    <div className="pointer-events-none fixed inset-0 z-[1] overflow-hidden">
      {petals.map((p, i) => (
        <span
          key={i}
          className="boda-petal"
          style={{
            left: `${p.left}%`,
            animation: `floatPetal ${p.duration}s linear ${p.delay}s infinite`,
            transform: `rotate(${p.rotate}deg)`,
            opacity: p.opacity,
          }}
        >
          <PetalSvg size={p.size} />
        </span>
      ))}
    </div>
  );
}

/* ============================================================
   FONDO
   ============================================================ */
function AmbienceBackdrop() {
  return (
    <>
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage: `radial-gradient(circle, ${T.gold} 0.7px, transparent 0.7px)`,
          backgroundSize: '32px 32px',
          opacity: 0.035,
        }}
      />
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          background: 'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(212,175,55,0.06), transparent 70%)',
        }}
      />
    </>
  );
}

/* ============================================================
   PARALLAX IMAGE
   ============================================================ */
function ParallaxImage({ src }) {
  const { ref, offset } = useParallax(0.5);
  return (
    <img
      ref={ref}
      src={src}
      alt=""
      className="absolute inset-0 w-full h-[120%] object-cover will-change-transform"
      style={{
        objectPosition: 'center 35%',
        transform: `translateY(${-offset}px) scale(1.06)`,
        transition: 'transform 0.15s linear',
      }}
    />
  );
}

/* ============================================================
   ICONO CIVIL
   ============================================================ */
const IconCityHall = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 21h16M4 21V10l8-4 8 4v11M9 21v-6h6v6" />
    <path d="M12 4V2M9 6.5h6" />
    <circle cx="12" cy="13" r="0.8" fill="currentColor" stroke="none" />
  </svg>
);

/* ============================================================
   HEADER EDITORIAL DE SECCIÓN
   ============================================================ */
function SectionHeader({ eyebrow, title, subtitle, align = 'center' }) {
  const isCenter = align === 'center';
  return (
    <div className={`mb-14 ${isCenter ? 'text-center' : 'text-left'}`}>
      <div className={`flex items-center gap-3 mb-4 ${isCenter ? 'justify-center' : ''}`}>
        <span className="w-8 h-px" style={{ background: T.gold }} />
        <span className="boda-eyebrow">{eyebrow}</span>
        <span className="w-8 h-px" style={{ background: T.gold }} />
      </div>
      {title && (
        <h2
          className="text-3xl sm:text-4xl md:text-5xl mb-5 boda-shimmer"
          style={{
            fontFamily: T.display,
            fontWeight: 500,
            letterSpacing: '0.02em',
            lineHeight: 1.15,
          }}
        >
          {title}
        </h2>
      )}
      {subtitle && (
        <p
          className="text-lg sm:text-xl italic max-w-2xl mx-auto"
          style={{ fontFamily: T.script, color: T.textDim, fontWeight: 400 }}
        >
          {subtitle}
        </p>
      )}
      <div className={`mt-6 flex ${isCenter ? 'justify-center' : ''}`}>
        <Ornament className="w-48 h-5" color={T.gold} />
      </div>
    </div>
  );
}

/* ============================================================
   COMPONENTE PRINCIPAL
   ============================================================ */
export default function BodaElegante({ data, title, media = [], audio = null, cover = null }) {
  useEffect(() => {
    const style = document.createElement('style');
    style.setAttribute('data-boda-elegante', 'true');
    style.innerHTML = GLOBAL_STYLES;
    document.head.appendChild(style);
    return () => style.remove();
  }, []);

  const d = data || {};
  const bride = d.bride || 'María';
  const groom = d.groom || 'Carlos';
  const initials = `${bride.charAt(0)} & ${groom.charAt(0)}`;
  const date = d.date ? new Date(d.date) : null;

  const dateShort = date
    ? date.toLocaleDateString('es-PE', { day: '2-digit', month: 'long', year: 'numeric' })
    : null;
  const dateTiny = date
    ? date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : null;
  const dayNum = date ? String(date.getDate()).padStart(2, '0') : null;
  const monthShort = date
    ? date.toLocaleDateString('es-PE', { month: 'short' }).toUpperCase().replace('.', '')
    : null;
  const yearNum = date ? date.getFullYear() : null;

  const hasCivil = !!(d.civilPlace || d.civilAddress);
  const hasChurch = !!(d.church || d.churchAddress);
  const hasReception = !!(d.place || d.address);
  const hasDetails = d.dressCode || d.giftRegistry || d.hashtag;

  const verse =
    d.verse ||
    'El amor es paciente, es bondadoso; todo lo cree, todo lo espera, todo lo soporta.';
  const verseRef = d.verseRef || '1 Corintios 13:4-7';

  return (
    <div
      className="min-h-screen w-full relative overflow-hidden"
      style={{ background: BG_MAIN, color: T.text, fontFamily: T.sans }}
    >
      <AmbienceBackdrop />
      <FloatingPetals count={18} />

      {/* ============================================================
          HERO CON PORTADA — Editorial cinematográfico
          ============================================================ */}
      {cover?.url ? (
        <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
          <ParallaxImage src={cover.url} />

          <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/30 to-black/70" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_25%,rgba(0,0,0,0.75)_100%)]" />

          {/* Marcos con esquinas decorativas */}
          <div
            className="absolute inset-5 sm:inset-8 md:inset-12 pointer-events-none"
            style={{ animation: 'pulseGlow 5s ease-in-out infinite' }}
          >
            <div className="absolute inset-0 border" style={{ borderColor: 'rgba(212,175,55,0.3)' }} />
            <CornerFrame className="absolute -top-px -left-px w-6 h-6 sm:w-8 sm:h-8" color={T.gold} />
            <CornerFrame className="absolute -top-px -right-px w-6 h-6 sm:w-8 sm:h-8 rotate-90" color={T.gold} />
            <CornerFrame className="absolute -bottom-px -left-px w-6 h-6 sm:w-8 sm:h-8 -rotate-90" color={T.gold} />
            <CornerFrame className="absolute -bottom-px -right-px w-6 h-6 sm:w-8 sm:h-8 rotate-180" color={T.gold} />
          </div>

          <div className="relative z-10 max-w-4xl mx-auto text-center px-6 py-24">
            <Reveal delay={0.1}>
              <div className="flex items-center justify-center gap-4 mb-10">
                <span className="w-10 h-px" style={{ background: T.gold, opacity: 0.6 }} />
                <p
                  style={{
                    fontFamily: T.sans,
                    fontSize: '10px',
                    letterSpacing: '0.55em',
                    textTransform: 'uppercase',
                    color: 'rgba(255,255,255,0.9)',
                    fontWeight: 500,
                  }}
                >
                  Nuestra Boda
                </p>
                <span className="w-10 h-px" style={{ background: T.gold, opacity: 0.6 }} />
              </div>
            </Reveal>

            <Reveal delay={0.25}>
              <WeddingRings
                color={T.gold}
                className="w-20 h-14 mx-auto mb-12 ring-anim"
                style={{ animation: 'ringPulse 5s ease-in-out infinite' }}
              />
            </Reveal>

            {/* Bloque de nombres editorial */}
            <div className="mb-8">
              <Reveal delay={0.4}>
                <h1
                  className="boda-shimmer"
                  style={{
                    fontFamily: T.display,
                    fontSize: 'clamp(2.75rem, 8vw, 6rem)',
                    lineHeight: 1,
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                    filter: 'drop-shadow(0 4px 40px rgba(0,0,0,0.6))',
                  }}
                >
                  {bride}
                </h1>
              </Reveal>

              <Reveal delay={0.5}>
                <div className="flex items-center justify-center gap-6 my-7 sm:my-9">
                  <span
                    className="h-px w-16 sm:w-24"
                    style={{ background: `linear-gradient(90deg, transparent, ${T.gold})` }}
                  />
                  <span
                    className="text-3xl sm:text-5xl italic"
                    style={{
                      fontFamily: T.script,
                      color: T.goldLight,
                      fontWeight: 400,
                      textShadow: `0 0 30px ${T.gold}80`,
                    }}
                  >
                    &amp;
                  </span>
                  <span
                    className="h-px w-16 sm:w-24"
                    style={{ background: `linear-gradient(90deg, ${T.gold}, transparent)` }}
                  />
                </div>
              </Reveal>

              <Reveal delay={0.6}>
                <h1
                  className="boda-shimmer"
                  style={{
                    fontFamily: T.display,
                    fontSize: 'clamp(2.75rem, 8vw, 6rem)',
                    lineHeight: 1,
                    fontWeight: 400,
                    letterSpacing: '0.01em',
                    filter: 'drop-shadow(0 4px 40px rgba(0,0,0,0.6))',
                  }}
                >
                  {groom}
                </h1>
              </Reveal>
            </div>

            {/* Fecha editorial */}
            {dateShort && (
              <Reveal delay={0.75}>
                <div className="mt-14 mb-6 flex items-center justify-center gap-5 sm:gap-7">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-px" style={{ background: 'rgba(255,255,255,0.35)' }} />
                    <span
                      style={{
                        fontFamily: T.sans,
                        fontSize: '10px',
                        letterSpacing: '0.4em',
                        textTransform: 'uppercase',
                        color: 'rgba(255,255,255,0.75)',
                        fontWeight: 400,
                      }}
                    >
                      Save the Date
                    </span>
                    <span className="w-8 h-px" style={{ background: 'rgba(255,255,255,0.35)' }} />
                  </div>
                </div>

                <div className="flex items-center justify-center gap-5 sm:gap-7">
                  <span
                    style={{
                      fontFamily: T.display,
                      fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                      color: '#fff',
                      fontWeight: 400,
                    }}
                  >
                    {dayNum}
                  </span>
                  <span className="w-px h-10 sm:h-12" style={{ background: `${T.gold}90` }} />
                  <span
                    style={{
                      fontFamily: T.serif,
                      fontSize: 'clamp(1rem, 2vw, 1.35rem)',
                      letterSpacing: '0.3em',
                      color: T.goldLight,
                      fontWeight: 500,
                    }}
                  >
                    {monthShort}
                  </span>
                  <span className="w-px h-10 sm:h-12" style={{ background: `${T.gold}90` }} />
                  <span
                    style={{
                      fontFamily: T.display,
                      fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                      color: '#fff',
                      fontWeight: 400,
                    }}
                  >
                    {yearNum}
                  </span>
                </div>
              </Reveal>
            )}

            {title && (
              <Reveal delay={0.9}>
                <p
                  className="mt-12 max-w-xl mx-auto italic"
                  style={{
                    fontFamily: T.script,
                    fontSize: '1.15rem',
                    color: 'rgba(255,255,255,0.78)',
                    fontWeight: 300,
                  }}
                >
                  "{title}"
                </p>
              </Reveal>
            )}
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
            <span
              style={{
                fontFamily: T.sans,
                fontSize: '9px',
                letterSpacing: '0.45em',
                color: 'rgba(212,175,55,0.75)',
                textTransform: 'uppercase',
              }}
            >
              Desliza
            </span>
            <div className="w-px h-14 relative overflow-hidden" style={{ background: 'rgba(212,175,55,0.2)' }}>
              <span
                className="absolute top-0 left-0 w-full h-4"
                style={{ background: `linear-gradient(180deg, transparent, ${T.gold})`, animation: 'scrollDot 2.4s ease-in-out infinite' }}
              />
            </div>
          </div>

          <div
            className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
            style={{ background: `linear-gradient(180deg, transparent 0%, rgba(8,8,15,0.6) 60%, ${T.bg} 100%)` }}
          />
        </section>
      ) : (
        /* ============================================================
           HERO SIN PORTADA — Editorial
           ============================================================ */
        <section className="relative px-6 pt-24 pb-24 sm:pt-32 sm:pb-32">
          <div className="max-w-3xl mx-auto text-center relative">
            <Reveal>
              <DecoFrameTop color={T.gold} className="w-full h-10 mb-12 opacity-50" />
            </Reveal>

            <Reveal delay={0.1}>
              <p className="boda-eyebrow mb-10">Nuestra Boda</p>
            </Reveal>

            <Reveal delay={0.2}>
              <WeddingRings
                color={T.gold}
                className="w-24 h-16 mx-auto mb-12 opacity-90"
                style={{ animation: 'ringPulse 5s ease-in-out infinite' }}
              />
            </Reveal>

            <Reveal delay={0.3}>
              <h1
                className="boda-shimmer"
                style={{
                  fontFamily: T.display,
                  fontSize: 'clamp(3rem, 9vw, 7rem)',
                  lineHeight: 1,
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                }}
              >
                {bride}
              </h1>
            </Reveal>

            <Reveal delay={0.4}>
              <div className="flex items-center justify-center gap-7 my-8">
                <span className="h-px w-24" style={{ background: `linear-gradient(90deg, transparent, ${T.gold})` }} />
                <span
                  className="text-4xl italic"
                  style={{ fontFamily: T.script, color: T.gold, fontWeight: 400 }}
                >
                  &amp;
                </span>
                <span className="h-px w-24" style={{ background: `linear-gradient(90deg, ${T.gold}, transparent)` }} />
              </div>
            </Reveal>

            <Reveal delay={0.5}>
              <h1
                className="boda-shimmer"
                style={{
                  fontFamily: T.display,
                  fontSize: 'clamp(3rem, 9vw, 7rem)',
                  lineHeight: 1,
                  fontWeight: 400,
                  letterSpacing: '0.01em',
                }}
              >
                {groom}
              </h1>
            </Reveal>

            <Reveal delay={0.6}>
              <div className="my-14 flex justify-center">
                <Flourish className="w-72 h-10" color={T.gold} />
              </div>
            </Reveal>

            {dateShort && (
              <Reveal delay={0.7}>
                <div className="inline-block">
                  <p className="boda-eyebrow mb-5">Save the Date</p>
                  <div className="flex items-center justify-center gap-5">
                    <span style={{ fontFamily: T.display, fontSize: '2rem', color: T.text, fontWeight: 400 }}>
                      {dayNum}
                    </span>
                    <span className="w-px h-10" style={{ background: `${T.gold}90` }} />
                    <span
                      style={{
                        fontFamily: T.serif,
                        fontSize: '1.15rem',
                        letterSpacing: '0.3em',
                        color: T.goldLight,
                        fontWeight: 500,
                      }}
                    >
                      {monthShort}
                    </span>
                    <span className="w-px h-10" style={{ background: `${T.gold}90` }} />
                    <span style={{ fontFamily: T.display, fontSize: '2rem', color: T.text, fontWeight: 400 }}>
                      {yearNum}
                    </span>
                  </div>
                </div>
              </Reveal>
            )}

            {title && (
              <Reveal delay={0.8}>
                <p
                  className="mt-12 text-xl italic max-w-2xl mx-auto"
                  style={{ fontFamily: T.script, color: T.textDim, fontWeight: 300 }}
                >
                  "{title}"
                </p>
              </Reveal>
            )}

            <Reveal delay={0.9}>
              <DecoFrameBottom color={T.gold} className="w-full h-10 mt-16 opacity-50" />
            </Reveal>
          </div>
        </section>
      )}

      {/* ============================================================
          VERSÍCULO — Editorial con capitular
          ============================================================ */}
      <section className="relative px-6 py-20 sm:py-24">
        <Reveal>
          <div className="max-w-3xl mx-auto">
            <div
              className="boda-card relative px-8 sm:px-16 md:px-20 py-16 sm:py-20"
              style={{ borderRadius: '2px' }}
            >
              {/* Esquinas decorativas */}
              <CornerFrame className="absolute top-3 left-3 w-7 h-7" color={`${T.gold}90`} />
              <CornerFrame className="absolute top-3 right-3 w-7 h-7 rotate-90" color={`${T.gold}90`} />
              <CornerFrame className="absolute bottom-3 left-3 w-7 h-7 -rotate-90" color={`${T.gold}90`} />
              <CornerFrame className="absolute bottom-3 right-3 w-7 h-7 rotate-180" color={`${T.gold}90`} />

              <div className="flex justify-center mb-8">
                <Ornament className="w-40 h-5" color={T.gold} />
              </div>

              <p
                className="boda-drop-cap text-xl sm:text-[1.4rem] leading-[1.85] text-center"
                style={{
                  fontFamily: T.script,
                  color: T.text,
                  fontWeight: 400,
                  fontStyle: 'italic',
                  textAlign: 'left',
                }}
              >
                {verse}
              </p>

              <div className="my-10 flex justify-center">
                <Flourish className="w-56 h-8" color={T.gold} />
              </div>

              <p
                className="text-center"
                style={{
                  fontFamily: T.sans,
                  fontSize: '11px',
                  letterSpacing: '0.45em',
                  textTransform: 'uppercase',
                  color: T.gold,
                  fontWeight: 500,
                }}
              >
                {verseRef}
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============================================================
          CUENTA REGRESIVA
          ============================================================ */}
      {date && (
        <section className="px-6 py-20 sm:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto">
              <SectionHeader
                eyebrow="Cuenta regresiva"
                title="Para nuestro gran día"
              />
              <Countdown target={date} accent={T.gold} labelColor={T.goldSoft} />
            </div>
          </Reveal>
        </section>
      )}

      {/* ============================================================
          MONOGRAMA
          ============================================================ */}
      <section className="px-6 py-16">
        <Reveal>
          <div className="max-w-md mx-auto text-center">
            <div style={{ animation: 'monogramFloat 7s ease-in-out infinite' }}>
              <DecoMonogram
                color={T.gold}
                initials={initials}
                className="w-44 h-44 mx-auto opacity-90"
              />
            </div>
          </div>
        </Reveal>
      </section>

      {/* ============================================================
          NUESTRA HISTORIA
          ============================================================ */}
      {d.story && (
        <section className="px-6 py-20 sm:py-24">
          <Reveal>
            <div className="max-w-2xl mx-auto text-center">
              <div className="flex justify-center mb-6">
                <div
                  className="w-12 h-12 grid place-items-center"
                  style={{ border: `1px solid ${T.borderStrong}`, color: T.gold }}
                >
                  <div className="w-5 h-5">
                    <IconBook className="w-full h-full" />
                  </div>
                </div>
              </div>
              <p className="boda-eyebrow mb-6">Nuestra Historia</p>
              <div className="flex justify-center mb-10">
                <Ornament className="w-48 h-5" color={T.gold} />
              </div>
              <p
                className="text-lg sm:text-xl leading-[1.95] italic whitespace-pre-line"
                style={{ fontFamily: T.script, color: T.text, fontWeight: 400, opacity: 0.95 }}
              >
                {d.story}
              </p>
            </div>
          </Reveal>
        </section>
      )}

      {/* ============================================================
          GALERÍA
          ============================================================ */}
      <Gallery
        media={media}
        category="Matrimonios"
        accent={T.gold}
        text={T.text}
        muted={T.muted}
        title="Recuerdos Eternos"
        subtitle="Nuestros Momentos"
        headingFont={T.serif}
      />

      {/* ============================================================
          CEREMONIAS — Cards editoriales
          ============================================================ */}
      {(hasCivil || hasChurch) && (
        <section className="px-6 py-20 sm:py-24">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <SectionHeader eyebrow="Itinerario" title="Ceremonias" />
            </Reveal>

            <div
              className={`grid gap-8 ${
                hasCivil && hasChurch ? 'md:grid-cols-2' : ''
              } max-w-3xl mx-auto`}
            >
              {hasCivil && (
                <Reveal delay={0.1}>
                  <div className="boda-card relative text-center px-8 py-14">
                    <div
                      className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3"
                      style={{ background: T.bg }}
                    >
                      <div
                        className="w-12 h-12 grid place-items-center"
                        style={{
                          border: `1px solid ${T.gold}`,
                          background: T.cardSolid,
                          color: T.gold,
                        }}
                      >
                        <div className="w-5 h-5">
                          <IconCityHall className="w-full h-full" />
                        </div>
                      </div>
                    </div>

                    <p className="boda-eyebrow mt-2 mb-6">Civil</p>

                    {d.civilPlace && (
                      <p
                        className="text-2xl mb-3 leading-snug"
                        style={{ fontFamily: T.display, color: T.text, fontWeight: 500 }}
                      >
                        {d.civilPlace}
                      </p>
                    )}
                    {d.civilAddress && (
                      <p
                        className="text-base mb-4 leading-relaxed"
                        style={{ fontFamily: T.script, color: T.textDim, fontWeight: 400 }}
                      >
                        {d.civilAddress}
                      </p>
                    )}
                    {d.civilTime && (
                      <div className="inline-flex items-center gap-3 mt-2">
                        <span className="w-6 h-px" style={{ background: T.gold }} />
                        <p
                          className="text-base"
                          style={{
                            fontFamily: T.sans,
                            color: T.goldLight,
                            fontWeight: 500,
                            letterSpacing: '0.15em',
                          }}
                        >
                          {d.civilTime}
                        </p>
                        <span className="w-6 h-px" style={{ background: T.gold }} />
                      </div>
                    )}
                  </div>
                </Reveal>
              )}

              {hasChurch && (
                <Reveal delay={0.2}>
                  <div className="boda-card relative text-center px-8 py-14">
                    <div
                      className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-3"
                      style={{ background: T.bg }}
                    >
                      <div
                        className="w-12 h-12 grid place-items-center"
                        style={{
                          border: `1px solid ${T.gold}`,
                          background: T.cardSolid,
                          color: T.gold,
                        }}
                      >
                        <div className="w-5 h-5">
                          <IconChurch className="w-full h-full" />
                        </div>
                      </div>
                    </div>

                    <p className="boda-eyebrow mt-2 mb-6">Religiosa</p>

                    {d.church && (
                      <p
                        className="text-2xl mb-3 leading-snug"
                        style={{ fontFamily: T.display, color: T.text, fontWeight: 500 }}
                      >
                        {d.church}
                      </p>
                    )}
                    {d.churchAddress && (
                      <p
                        className="text-base mb-4 leading-relaxed"
                        style={{ fontFamily: T.script, color: T.textDim, fontWeight: 400 }}
                      >
                        {d.churchAddress}
                      </p>
                    )}
                    {(d.churchTime || d.time) && (
                      <div className="inline-flex items-center gap-3 mt-2">
                        <span className="w-6 h-px" style={{ background: T.gold }} />
                        <p
                          className="text-base"
                          style={{
                            fontFamily: T.sans,
                            color: T.goldLight,
                            fontWeight: 500,
                            letterSpacing: '0.15em',
                          }}
                        >
                          {d.churchTime || d.time}
                        </p>
                        <span className="w-6 h-px" style={{ background: T.gold }} />
                      </div>
                    )}
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          RECEPCIÓN — Card editorial destacada
          ============================================================ */}
      {hasReception && (
        <section className="px-6 py-20 sm:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto">
              <div className="boda-card relative text-center px-8 sm:px-16 py-16 sm:py-20">
                <CornerFrame className="absolute top-4 left-4 w-7 h-7" color={`${T.gold}90`} />
                <CornerFrame className="absolute top-4 right-4 w-7 h-7 rotate-90" color={`${T.gold}90`} />
                <CornerFrame className="absolute bottom-4 left-4 w-7 h-7 -rotate-90" color={`${T.gold}90`} />
                <CornerFrame className="absolute bottom-4 right-4 w-7 h-7 rotate-180" color={`${T.gold}90`} />

                <div className="flex justify-center mb-6">
                  <div
                    className="w-14 h-14 grid place-items-center"
                    style={{ border: `1px solid ${T.borderStrong}`, color: T.gold }}
                  >
                    <div className="w-6 h-6">
                      <IconMapPin className="w-full h-full" />
                    </div>
                  </div>
                </div>

                <p className="boda-eyebrow mb-6">Recepción</p>

                {d.place && (
                  <p
                    className="text-3xl sm:text-4xl mb-5 leading-tight"
                    style={{ fontFamily: T.display, color: T.text, fontWeight: 500 }}
                  >
                    {d.place}
                  </p>
                )}
                {d.address && (
                  <p
                    className="text-lg mb-6 leading-relaxed max-w-xl mx-auto"
                    style={{ fontFamily: T.script, color: T.textDim, fontWeight: 400 }}
                  >
                    {d.address}
                  </p>
                )}
                {d.receptionTime && (
                  <div className="inline-flex items-center gap-3 mt-2">
                    <span className="w-8 h-px" style={{ background: T.gold }} />
                    <p
                      className="text-base"
                      style={{
                        fontFamily: T.sans,
                        color: T.goldLight,
                        fontWeight: 500,
                        letterSpacing: '0.15em',
                      }}
                    >
                      {d.receptionTime}
                    </p>
                    <span className="w-8 h-px" style={{ background: T.gold }} />
                  </div>
                )}
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* ============================================================
          DRESS CODE / REGALOS / HASHTAG
          ============================================================ */}
      {hasDetails && (
        <section className="px-6 py-20 sm:py-24">
          <div className="max-w-5xl mx-auto">
            <Reveal>
              <SectionHeader eyebrow="Detalles" />
            </Reveal>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {d.dressCode && (
                <Reveal delay={0.1}>
                  <div className="boda-card text-center px-6 py-12 flex flex-col items-center h-full">
                    <div
                      className="w-16 h-16 grid place-items-center mb-6"
                      style={{ border: `1px solid ${T.borderStrong}`, color: T.gold }}
                    >
                      <div className="w-6 h-6">
                        <IconShirt className="w-full h-full" />
                      </div>
                    </div>
                    <p className="boda-eyebrow mb-4">Dress Code</p>
                    <p
                      className="text-base leading-relaxed"
                      style={{ fontFamily: T.script, color: T.text, fontWeight: 400 }}
                    >
                      {d.dressCode}
                    </p>
                  </div>
                </Reveal>
              )}

              {d.giftRegistry && (
                <Reveal delay={0.2}>
                  <div className="boda-card text-center px-6 py-12 flex flex-col items-center h-full">
                    <div
                      className="w-16 h-16 grid place-items-center mb-6"
                      style={{ border: `1px solid ${T.borderStrong}`, color: T.gold }}
                    >
                      <div className="w-6 h-6">
                        <IconGift className="w-full h-full" />
                      </div>
                    </div>
                    <p className="boda-eyebrow mb-4">Mesa de Regalos</p>
                    <a
                      href={d.giftRegistry}
                      target="_blank"
                      rel="noreferrer"
                      className="boda-btn mt-2"
                    >
                      Ver lista
                    </a>
                  </div>
                </Reveal>
              )}

              {d.hashtag && (
                <Reveal delay={0.3}>
                  <div className="boda-card text-center px-6 py-12 flex flex-col items-center h-full">
                    <div
                      className="w-16 h-16 grid place-items-center mb-6"
                      style={{ border: `1px solid ${T.borderStrong}`, color: T.gold }}
                    >
                      <div className="w-6 h-6">
                        <IconCamera className="w-full h-full" />
                      </div>
                    </div>
                    <p className="boda-eyebrow mb-4">Hashtag</p>
                    <p
                      className="text-base leading-snug boda-gold-text"
                      style={{
                        fontFamily: T.display,
                        fontWeight: 500,
                        fontSize: '1.15rem',
                      }}
                    >
                      {d.hashtag}
                    </p>
                  </div>
                </Reveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ============================================================
          MENSAJE
          ============================================================ */}
      {d.description && (
        <section className="px-6 py-20 sm:py-24">
          <Reveal>
            <div className="max-w-2xl mx-auto text-center">
              <div className="flex justify-center mb-8">
                <Flourish className="w-64 h-10" color={T.gold} />
              </div>

              <p className="boda-eyebrow mb-8">Un mensaje para ti</p>

              <p
                className="text-2xl sm:text-3xl leading-[1.7] italic"
                style={{
                  fontFamily: T.display,
                  color: T.text,
                  fontWeight: 400,
                  fontStyle: 'italic',
                }}
              >
                "{d.description}"
              </p>

              <div className="flex justify-center mt-10">
                <Ornament className="w-48 h-5" color={T.gold} />
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* ============================================================
          NOTAS
          ============================================================ */}
      {d.notes && (
        <section className="px-6 py-10">
          <Reveal>
            <div className="max-w-xl mx-auto text-center">
              <p
                className="text-sm leading-relaxed"
                style={{ fontFamily: T.script, color: T.muted, fontWeight: 400 }}
              >
                {d.notes}
              </p>
            </div>
          </Reveal>
        </section>
      )}

      {/* ============================================================
          CIERRE
          ============================================================ */}
      <section className="relative px-6 py-24 text-center">
        <Reveal>
          <DecoScallop color={T.gold} className="w-full h-8 mb-14 opacity-40" />
        </Reveal>

        <Reveal delay={0.1}>
          <WeddingRings
            color={T.gold}
            className="w-20 h-14 mx-auto mb-10 opacity-85"
            style={{ animation: 'ringPulse 5s ease-in-out infinite' }}
          />
        </Reveal>

        <Reveal delay={0.2}>
          <p
            className="text-2xl sm:text-3xl italic max-w-2xl mx-auto leading-[1.7]"
            style={{ fontFamily: T.script, color: T.text, fontWeight: 400 }}
          >
            Tu presencia es el regalo más valioso.
            <br />
            <span style={{ color: T.goldLight }}>¡Te esperamos para celebrar juntos!</span>
          </p>
        </Reveal>

        {dateTiny && (
          <Reveal delay={0.3}>
            <div className="mt-14 flex items-center justify-center gap-4">
              <span className="w-8 h-px" style={{ background: `${T.gold}70` }} />
              <p
                style={{
                  fontFamily: T.sans,
                  fontSize: '11px',
                  letterSpacing: '0.45em',
                  textTransform: 'uppercase',
                  color: T.gold,
                  fontWeight: 500,
                }}
              >
                {bride} &amp; {groom} · {dateTiny}
              </p>
              <span className="w-8 h-px" style={{ background: `${T.gold}70` }} />
            </div>
          </Reveal>
        )}
      </section>

      {/* ============================================================
          FOOTER
          ============================================================ */}
      <footer className="pb-14 text-center">
        <div className="flex justify-center mb-5">
          <Ornament className="w-40 h-5 opacity-60" color={T.gold} />
        </div>
        <p
          style={{
            fontFamily: T.sans,
            fontSize: '10px',
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: T.goldSoft,
            fontWeight: 400,
          }}
        >
          Invitación creada con{' '}
          <b style={{ color: T.gold, fontWeight: 600, letterSpacing: '0.32em' }}>Go Fiesta</b>
        </p>
      </footer>

      {/* ============================================================
          AUDIO
          ============================================================ */}
      {audio && <AudioPlayer audio={audio} accent={T.gold} />}
    </div>
  );
}